import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import { LANGUAGE_EN } from '@home/_config/config';
import { IDonutChartProps } from './type';
import './index.scss';

const EMPTY_CHART_DATA = [{ name: '', value: 1, share: 0 }];
const EMPTY_LEGEND: string[] = [];
const EMPTY_COLOR = ['#E5E6EB'];

const DonutChart: React.FC<IDonutChartProps> = (props) => {
  const {
    segments = [],
    size = 80,
    className = '',
    tooltipTitle,
    tooltipTitleCn,
    segmentValues,
    formatRule = 'thousands2Decimal',
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const colorArr = useMemo(() => segments.map((seg) => seg.color), [segments]);

  const { chartData, legendData, isNodata } = useMemo(() => {
    const legendData: string[] = [];
    const chartData: { name: string; value: number; share: number; }[] = [];
    let hasValue = false;

    segments.forEach((seg) => {
      const displayLabel = isEn ? seg.label : (seg.labelCn || seg.label);
      legendData.push(displayLabel);
      const rawValue = segmentValues?.[seg.metricKey || ''];
      const numValue = rawValue !== undefined && rawValue !== null ? Number(rawValue) : 0;
      if (numValue > 0) hasValue = true;
      chartData.push({
        name: displayLabel,
        value: numValue,
        share: seg.value / 100,
      });
    });

    return { chartData, legendData, isNodata: !hasValue };
  }, [segments, segmentValues, isEn]);

  const getTooltipText = (params: Record<string, unknown>): string => {
    const { name, color, value } = params as { name: string; color: string; value: number; };
    const segment = segments.find((s) => {
      const displayLabel = isEn ? s.label : (s.labelCn || s.label);
      return displayLabel === name;
    });
    const share = segment ? segment.value / 100 : 0;
    const resolvedTooltipTitle = isEn ? (tooltipTitle || '') : (tooltipTitleCn || tooltipTitle || '');

    return getEchartTooltipString({
      table: {
        data: [{ name, value, color, share }],
        columns: [
          {
            key: 'name',
            header: resolvedTooltipTitle,
            showIcon: true,
          },
          {
            key: 'value',
            header: '',
            dataFormatRule: formatRule,
            align: 'right',
          },
          {
            key: 'share',
            header: '',
            dataFormatRule: 'percent2Decimal',
            align: 'right',
          },
        ],
      },
    });
  };

  const setOtherConfig = (opts: Record<string, unknown>): Record<string, unknown> => {
    const newOpts = { ...opts } as Record<string, unknown>;
    const series = (newOpts.series as Record<string, unknown>[]);
    if (series?.[0]) {
      const seriesData = (series[0] as Record<string, unknown>).data as Record<string, unknown>[];
      if (seriesData) {
        seriesData.forEach((item) => {
          item.label = { show: false };
          item.labelLine = { show: false };
        });
      }
      series[0] = {
        ...series[0],
        radius: ['45%', '70%'],
        center: ['50%', '50%'],
        label: { show: false },
        labelLine: { show: false },
        emphasis: isNodata ? { disabled: true } : { scaleSize: 6 },
      };
    }
    newOpts.legend = { show: false };
    newOpts.graphic = [];
    if (isNodata) {
      newOpts.tooltip = { show: false };
    } else {
      const existingTooltip = (newOpts.tooltip || {}) as Record<string, unknown>;
      newOpts.tooltip = {
        ...existingTooltip,
        confine: false,
        appendToBody: true,
      };
    }
    return newOpts;
  };

  const containerSize = size + 12;

  return (
    <div className={`dbh-casual-donut-chart ${className}`} style={{ width: containerSize, height: containerSize }}>
      <PieChart
        className="dbh-casual-donut-chart__pie"
        height={size}
        isNodata={false}
        chartData={isNodata ? EMPTY_CHART_DATA : chartData}
        legendData={isNodata ? EMPTY_LEGEND : legendData}
        colorArr={isNodata ? EMPTY_COLOR : colorArr}
        formatterFunc={getTooltipText}
        setOtherConfigFunc={setOtherConfig}
        isShowLegend={false}
      />
    </div>
  );
};

export default DonutChart;
